import { Component, OnInit } from '@angular/core';
import { NgRedux } from '@redux/redux-compatibility.service';
import { ReferenceMappingService } from 'src/app/shared/services/reference-mapping.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/shared/redux/store';

@Component({
    selector: 'reference-mapping',
    templateUrl: './reference-mapping.component.html',
    styleUrls: ['./reference-mapping.component.scss'],
    standalone: false
})
export class ReferenceMappingComponent implements OnInit {
  private readonly REFERENCE_TEXT_MAX_LENGTH = 100;
  data: any[] = [];
  existingTexts: { label: string; value: string; fullText: string }[] = [];
  loading: boolean = true;
  isEditMode: boolean = false;
  groupedData: {
    document_id: number;
    document_name: string;
    references: any[];
  }[] = [];
  referenceMap = new Map<string, number>();

  constructor(
    private referenceMappingService: ReferenceMappingService,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private ngRedux: NgRedux<AppState>
  ) {}

  ngOnInit(): void {
    this.getData();
    this.getExistingReferenceTexts();
  }

  getData(): void {
    const catVersionId = this.ngRedux.getState().curCat.catVersionId;
    this.referenceMappingService.getDocuments(catVersionId).subscribe(
      (data) => {
        console.log('Full data received:', data);
        this.data = data.map(item => ({
          ...item,
          isNewReference: false  
        }));
        console.log('Data received:', JSON.stringify(this.data));
        this.groupDataByDocumentName();
        this.initializeReferenceMap();
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching data:', error);
        this.loading = false;
      }
    );
  }
  

  getExistingReferenceTexts(): void {
    const catId = this.ngRedux.getState().curCat.catId;
    this.referenceMappingService.getExistingTexts(catId).subscribe(
      (texts) => {
        this.existingTexts = texts.map(text => ({
          label: text.length > this.REFERENCE_TEXT_MAX_LENGTH ? text.substring(0, this.REFERENCE_TEXT_MAX_LENGTH) + '...' : text,
          value: text,
          fullText: text
        }));
        console.log('Existing texts received:', JSON.stringify(this.existingTexts));
      },
      (error) => {
        console.error('Error fetching texts:', error);
      }
    );
  }

  onReferenceChange(event: any, row: any) {
    const selectedValue = event.target.value; 
    console.log("Selected value:", selectedValue);
  
    if (selectedValue === 'new') {
      row.isNewReference = true;
      row.reference_text = '';
    } else {
      row.isNewReference = false;
    }
  }
  
  cancelNewReference(row: any) {
    row.isNewReference = false;
    row.reference_text = '';
  }
  
  onFieldValueChanged(event: any) {
    if (event) {
      (event.originalEvent as MouseEvent).stopPropagation();
    }
    
    console.log("Selected reference:", event.value);

  }

  toggleEditMode(row: any): void {
    row.isEditMode = !row.isEditMode;
    if (!row.isEditMode) {
      row.reference_text = row.originalReferenceText;
    } else {
      row.originalReferenceText = row.reference_text;
    }
  }


  saveReferenceText(row: any): void {
    const catVersionId = this.ngRedux.getState().curCat.catVersionId;
    const obs = row.originalReferenceText !== ""
      ? this.referenceMappingService.updateReferenceMapping(row.document_id, row.reference_text, catVersionId, row.id)
      : this.referenceMappingService.createReferenceMapping(row.document_id, row.document_name, row.reference_text, catVersionId);
    obs.subscribe(
      () => {
        console.log('Reference text updated successfully!');
        row.isEditMode = false;
        row.originalReferenceText = row.reference_text;
        this.getData();
      },
      (error) => {
        console.error('Error updating reference text:', error);
      }
    );
  }

  deleteReference(row: any): void {
    if (!row.id) return;

    let validationStr = this.translateService.instant(
      'translate.alert.referenceDeleteConfirm'
    );

    this.sharedService.emitPopupchReceivedEvent({
      icon: 'exclamation-triangle',
      subTitle: validationStr,
      keep: false,
      approve: () => {
        const catVersionId = this.ngRedux.getState().curCat.catVersionId;
        this.referenceMappingService.deleteReferenceMapping(row.id, catVersionId).subscribe(
          () => {
            this.sharedService.alertSuccess(
              this.translateService.instant('translate.alert.referenceDeleted')
            );
            this.getData();
          },
          (error) => {
            console.error('Error deleting reference:', error);
            this.sharedService.alertError(
              this.translateService.instant('translate.alert.referenceTextUpdateError')
            );
          }
        );
      },
    });
  }

  groupDataByDocumentName() {
    const groupedMap = new Map<number, { document_id: number, document_name: string, references: any[], collapsed?: boolean }>();
    for (const item of this.data) {
      const key = item.document_id;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          document_id: item.document_id,
          document_name: item.document_name,
          references: [],
          collapsed: false
        });
      }

      groupedMap.get(key)!.references.push(item);
    }

    this.groupedData = Array.from(groupedMap.values());
    this.groupedData.forEach(group => {
      this.addPlaceholder(group);
    });
  }

  initializeReferenceMap() {
    this.referenceMap.clear();

    this.data.forEach(entry => {
      const key = this.getLookupKey(entry.document_id, entry.reference_text);
      this.referenceMap.set(key, entry.id);
    });
  }

  getLookupKey(document_id: number, reference_text: string): string {
    return `${document_id}::${reference_text.trim().toLowerCase()}`;
  }

  getReferenceId(document_id: number, reference_text: string): number | undefined {
    const key = this.getLookupKey(document_id, reference_text);
    return this.referenceMap.get(key);
  }
  
  addPlaceholder(group: any): void {
    const hasEmptyReference = group.references.some(ref => !ref.reference_text?.trim());
    if (hasEmptyReference) {
      return;
    }
    group.references.push({
      document_id: group.document_id,
      document_name: group.document_name,
      reference_text: '',
      isEditMode: false,
      isNewReference: false,
      isPlaceholder: true
    });
  }
}
  